import { useState } from 'react';
import { ChevronRightOutlined } from '@mui/icons-material';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { DialogSurface, SectionCard, designTokens } from '../../design-system';
import { AbsenceBalanceBreakdown } from './AbsenceBalanceBreakdown';
import { SelfRequestableLeaveList } from './SelfRequestableLeaveList';
import { absenceTypeBalance } from './absenceTypeBalance';
import type { AbsenceRequestableTimeOffType, AbsenceTimeOffBalance, AbsenceTimeOffType } from './model';

const amount = (value: string, unit: string) => `${Number(value).toLocaleString('ko-KR', { maximumFractionDigits: 4 })}${unit === 'HOURS' ? '시간' : unit === 'MINUTES' ? '분' : '일'}`;
const unavailableMessage = '잔액을 확인하지 못했습니다. 잠시 후 다시 조회해 주세요.';

export function SelfLeaveBalanceCard({ balances, types, limitTypes, canRequest, onRequest }: {
  balances: AbsenceTimeOffBalance[];
  types: AbsenceTimeOffType[];
  limitTypes: AbsenceRequestableTimeOffType[];
  canRequest: boolean;
  onRequest: (typeId: string) => void;
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const options = [...new Set(balances.map((balance) => balance.timeOffType.id))].map((typeId) => {
    const balance = absenceTypeBalance(balances, typeId);
    return { typeId, balance, title: types.find((type) => type.id === typeId)?.descriptor || balance?.timeOffType.descriptor || '휴가' };
  });
  const singleBalance = options.length === 1 ? options[0].balance : undefined;
  return <>
    <SectionCard title={options.length === 1 ? options[0].title : '휴가 잔여'} showDivider={false}>
      <Stack spacing={1.5} sx={{ px: `${designTokens.layout.surfacePaddingCompact}px`, pb: `${designTokens.layout.surfacePaddingCompact}px` }}>
        {options.length ? options.map((option) => <Stack key={option.typeId} spacing={0.5}>
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'baseline' }}>
            {options.length > 1 && <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.title}</Typography>}
            <Typography variant="body2" data-testid="absence-balance-value" sx={{ fontWeight: 700 }}>사용 가능 {option.balance ? amount(option.balance.available, option.balance.unitOfTime) : '확인 필요'}</Typography>
            {option.balance && <Typography variant="body2" color="text.secondary">· 승인 대기 {amount(option.balance.pending, option.balance.unitOfTime)}</Typography>}
          </Stack>
          {!option.balance && <Typography variant="caption" color="text.secondary">{unavailableMessage}</Typography>}
        </Stack>) : <Typography variant="body2" color="text.secondary">부여된 잔액이 없습니다.</Typography>}
        {singleBalance && singleBalance.policyCount > 1 && <Typography variant="caption" color="text.secondary">{singleBalance.policyCount}개 정책 합산 · 만료가 빠른 잔액부터 사용</Typography>}
        {options.length > 0 && <Button variant="text" color="inherit" fullWidth aria-haspopup="dialog" endIcon={<ChevronRightOutlined />} onClick={() => setDetailsOpen(true)}>잔여량 계산 내역</Button>}
        {limitTypes.length > 0 && <>
          <Divider />
          <SelfRequestableLeaveList types={limitTypes} canRequest={canRequest} onRequest={onRequest} />
        </>}
      </Stack>
    </SectionCard>
    <DialogSurface open={detailsOpen} onClose={() => setDetailsOpen(false)} title="잔여량 계산 내역" closeLabel="잔여량 계산 내역 닫기" size="standard" mobilePresentation="fullscreen">
      <Stack spacing={3} divider={<Divider />}>
        {options.map((option) => <Box component="section" aria-label={option.title} key={option.typeId}>
          <Typography variant="h6" component="h3">{option.title}</Typography>
          {option.balance ? <>
            <AbsenceBalanceBreakdown balance={option.balance} collapsible={false} />
            {option.balance.policyCount > 1 && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{option.balance.policyCount}개 정책 합산 · 만료가 빠른 잔액부터 사용</Typography>}
          </> : <Typography variant="body2" color="text.secondary">{unavailableMessage}</Typography>}
        </Box>)}
      </Stack>
    </DialogSurface>
  </>;
}
